/**
 * **Integrasi** — kunci M2M milik tenant: skrip laporan, BI, dan server MCP.
 *
 * ### ⛔ Kenapa layar ini akhirnya dibuat
 * Jalur M2M lengkap sejak Fase 0, tapi satu-satunya cara mendaftarkan klien adalah CLI **di dalam
 * kotak servernya**. ⇒ Tiap integrasi harus lewat operator, lewat SSH. Yang terjadi pada sistem
 * begitu bisa ditebak: saat seseorang butuh akses cepat, ia diberi kredensial klien yang sudah ada
 * — yang scope-nya lebih luas, dan yang mencabutnya akan mematikan integrasi lain juga.
 * **Kunci bersama adalah kunci yang tak pernah dicabut.**
 *
 * ### ⭐⭐⭐ Kunci privat ditampilkan SEKALI
 * Server menyimpan yang publik saja. Layar ini menampilkan yang privat satu kali, dengan peringatan
 * yang menyebut akibatnya — bukan "simpan baik-baik" yang sopan tapi tak berarti apa-apa.
 *
 * ### ⚠️ Scope yang tak ada di daftar BUKAN karena layarnya belum jadi
 * `platform:*`, `pos:operate`, dan `master:write` sengaja tak bisa diberikan dari sini; alasannya
 * ada di `TENANT_GRANTABLE_SCOPES` (kontrak) dan **ditegakkan server**, bukan oleh daftar ini.
 */
import { Alert, Button, Card, Checkbox, Form, Input, Modal, Space, Switch, Table, Tag, Typography, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { IntegrationClient } from '../api/client';
import { canWrite } from '../api/session';

/**
 * ⚠️ Disalin dengan sadar dari `TENANT_GRANTABLE_SCOPES` — portal-web tak memuat `@osbpos/contract`
 * sebagai dependensi (alasan yang sama dengan daftar izin di `Staff.tsx`). Yang menjaga agar daftar
 * ini tak menua diam-diam: server **menolak** apa pun di luar daftarnya sendiri.
 */
const SCOPES = ['master:read', 'reports:read', 'ops:read'] as const;

export function Integrations() {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [rows, setRows] = useState<readonly IntegrationClient[]>([]);
  const [buat, setBuat] = useState(false);
  const [form] = Form.useForm();
  /** Kunci yang baru terbit — hidup di memori layar saja, tak pernah disimpan ke mana pun. */
  const [kunci, setKunci] = useState<{ clientId: string; privateJwk: unknown } | null>(null);

  const muat = useCallback(async () => {
    setRows(await api.integrationClients());
  }, []);

  useEffect(() => { void muat().catch(() => undefined); }, [muat]);

  const jalankan = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await muat();
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Typography.Title level={4} style={{ margin: 0 }}>{t('integration.title')}</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
        {t('integration.subtitle')}
      </Typography.Paragraph>

      {/* ⛔ Bukan basa-basi: yang membaca layar ini sedang memutuskan memberi kunci ke pihak luar. */}
      <Alert type="warning" showIcon title={t('integration.scopeWhy')} data-testid="integrasi-catatan" />

      {boleh && (
        <Button type="primary" data-testid="integrasi-baru" onClick={() => { setBuat(true); }}>
          {t('integration.add')}
        </Button>
      )}

      <Table<IntegrationClient>
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={[...rows]}
        data-testid="integrasi-daftar"
        columns={[
          {
            title: t('integration.client'),
            render: (_: unknown, r: IntegrationClient) => (
              <Space>
                <code data-testid={`integrasi-kode-${r.id}`}>{r.clientId}</code>
                {/* ⭐ Agen AI ditandai dari DATA (`is_agent`), bukan dari ejaan namanya. */}
                {r.isAgent && <Tag color="purple">{t('integration.agent')}</Tag>}
              </Space>
            ),
          },
          {
            title: t('integration.scopes'),
            render: (_: unknown, r: IntegrationClient) =>
              r.scopes.map((s) => <Tag key={s}>{s}</Tag>),
          },
          {
            title: t('outlets.status'),
            width: 130,
            render: (_: unknown, r: IntegrationClient) => (
              <Switch
                size="small"
                disabled={!boleh}
                checked={r.status === 'active'}
                data-testid={`integrasi-aktif-${r.id}`}
                onChange={(v) => void jalankan(() => api.setIntegrationStatus(r.id, v))}
              />
            ),
          },
        ]}
      />

      <Modal
        open={buat}
        title={t('integration.add')}
        okText={t('integration.create')}
        onCancel={() => { setBuat(false); form.resetFields(); }}
        onOk={() => {
          void form.validateFields().then(async (v: Record<string, unknown>) => {
            try {
              const hasil = await api.createIntegrationClient({
                name: String(v['name'] ?? '').trim().toLowerCase(),
                scopes: (v['scopes'] as string[] | undefined) ?? [],
                isAgent: v['isAgent'] === true,
              });
              setBuat(false);
              form.resetFields();
              /* ⭐⭐ Kunci muncul SEKARANG dan tak akan muncul lagi — dialognya menyebut itu. */
              setKunci(hasil);
              await muat();
            } catch (e) {
              void message.error(e instanceof Error ? e.message : String(e));
            }
          });
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t('integration.name')}
            tooltip={t('integration.nameHint')}
            rules={[{ required: true, pattern: /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/, message: t('integration.nameRule') }]}
          >
            <Input maxLength={40} data-testid="integrasi-nama" />
          </Form.Item>
          <Form.Item name="scopes" label={t('integration.scopes')} rules={[{ required: true }]}>
            <Checkbox.Group data-testid="integrasi-scope">
              <Space direction="vertical">
                {SCOPES.map((s) => (
                  <Checkbox key={s} value={s} data-testid={`integrasi-scope-${s}`}>
                    <code>{s}</code> — {t(`integration.scope.${s}`)}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item
            name="isAgent"
            label={t('integration.agentQ')}
            valuePropName="checked"
            tooltip={t('integration.agentHint')}
          >
            <Switch data-testid="integrasi-agen" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={kunci !== null}
        title={t('integration.keyTitle')}
        footer={<Button type="primary" onClick={() => { setKunci(null); }}>{t('integration.keySaved')}</Button>}
        closable={false}
        width={640}
      >
        {/*
          ⛔⛔ Peringatan menyebut AKIBATNYA, bukan "simpan baik-baik". Yang hilang tak bisa
          dipulihkan karena server memang tak menyimpannya — jalan satu-satunya membuat klien baru.
        */}
        <Alert type="error" showIcon style={{ marginBottom: 12 }} title={t('integration.keyOnce')} />
        <Typography.Paragraph>
          <code data-testid="integrasi-client-id">{kunci?.clientId}</code>
        </Typography.Paragraph>
        <Card size="small">
          <Typography.Paragraph
            copyable={{ text: JSON.stringify(kunci?.privateJwk) }}
            style={{ margin: 0, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12 }}
            data-testid="integrasi-kunci"
          >
            {JSON.stringify(kunci?.privateJwk)}
          </Typography.Paragraph>
        </Card>
      </Modal>
    </Space>
  );
}
